import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Award, CheckCircle2, Download, Handshake, Inbox, Paperclip, Send } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api, API, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/i18n";
import { toast } from "sonner";

const field =
  "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none";

const EMPTY = { company: "", contact_name: "", email: "", phone: "", business_type: "", message: "", logo: "" };
const BUSINESS_TYPES = ["Reseller / Retail", "NGO / Non-profit", "Government", "Corporate", "Education", "Other"];

function LogoPicker({ value, onChange, testId, uploadLabel, uploadingLabel }) {  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload/logo", fd);
      onChange(res.data.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-4">
      {value && (
        <img src={imgUrl(value)} alt="logo" data-testid={`${testId}-preview`}
          className="h-12 max-w-32 object-contain bg-white p-1.5 border border-white/10" />
      )}
      <label
        data-testid={`${testId}-btn`}
        className="inline-flex items-center gap-2 border border-white/15 text-xs text-slate-300 hover:border-[#ED1C24] hover:text-white px-4 py-2.5 cursor-pointer transition-colors duration-300"
      >
        {uploading ? uploadingLabel : uploadLabel}
        <input type="file" accept="image/*" className="hidden" onChange={pick} />
      </label>
    </div>
  );
}

function FilePicker({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload/document", fd);
      onChange({ url: res.data.url, filename: res.data.filename });
      toast.success("File attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label
        data-testid={`${testId}-btn`}
        className="inline-flex items-center gap-2 border border-white/15 text-xs text-slate-300 hover:border-[#ED1C24] hover:text-white px-4 py-2.5 cursor-pointer transition-colors duration-300"
      >
        <Paperclip size={13} />
        {uploading ? "Uploading..." : "Attach file (optional)"}
        <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" className="hidden" onChange={pick} />
      </label>
      {value && (
        <span data-testid={`${testId}-name`} className="text-xs text-emerald-400 flex items-center gap-2">
          {value.filename}
          <button type="button" data-testid={`${testId}-clear`} onClick={() => onChange(null)}
            className="text-slate-500 hover:text-[#ED1C24]">×</button>
        </span>
      )}
    </div>
  );
}

export default function Partner() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [appNumber, setAppNumber] = useState(null);
  const [certRef, setCertRef] = useState("");
  const [certEmail, setCertEmail] = useState("");
  const [certState, setCertState] = useState(null); // null | "searching" | "done" | "not_found" | "not_ready"
  const [dashRef, setDashRef] = useState("");
  const [dashEmail, setDashEmail] = useState("");
  const [dash, setDash] = useState(null);
  const [dashState, setDashState] = useState(null); // null | "loading" | "error"
  const [reqForm, setReqForm] = useState({ title: "", description: "", attachment: null });
  const [reqSending, setReqSending] = useState(false);
  const { t } = useLang();
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#dashboard") {
      setTimeout(() => {
        document.getElementById("partner-dashboard")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, [location.hash]);

  const loadDashboard = async (e) => {
    e?.preventDefault();
    setDashState("loading");
    try {
      const res = await api.get("/partner-requests", {
        params: { app_number: dashRef.trim(), email: dashEmail.trim() },
      });
      setDash(res.data);
      setDashState(null);
    } catch (err) {
      setDash(null);
      setDashState("error");
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setReqSending(true);
    try {
      await api.post("/partner-requests", {
        app_number: dashRef.trim(), email: dashEmail.trim(),
        title: reqForm.title, description: reqForm.description,
        attachment: reqForm.attachment?.url || "",
      });
      toast.success(t("pdash_success"));
      setReqForm({ title: "", description: "", attachment: null });
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("partner_error_toast"));
    } finally {
      setReqSending(false);
    }
  };

  const saveLogo = async (url) => {
    try {
      await api.post("/partner-logo", { app_number: dashRef.trim(), email: dashEmail.trim(), logo: url });
      toast.success(t("plogo_done"));
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const statusColor = {
    pending: "text-[#FFC61A] border-[#FFC61A]/40",
    in_review: "text-sky-400 border-sky-400/40",
    approved: "text-emerald-400 border-emerald-400/40",
    rejected: "text-[#ED1C24] border-[#ED1C24]/40",
    new: "text-slate-400 border-white/20",
  };

  const downloadCertificate = async (e) => {
    e.preventDefault();
    setCertState("searching");
    try {
      const res = await api.get(`/partner-certificates/${encodeURIComponent(certRef.trim())}`, {
        params: { email: certEmail.trim() },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KINOS-Partnership-${certRef.trim().toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setCertState("done");
    } catch (err) {
      setCertState(err.response?.status === 409 ? "not_ready" : "not_found");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post("/partner-applications", form);
      setAppNumber(res.data.app_number);
      toast.success(t("partner_success_toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("partner_error_toast"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="partner-page" className="bg-[#0B1120] min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-3xl">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <Handshake size={20} className="text-[#FFC61A]" />
            <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{t("partner_label")}</p>
          </div>
          <h1 className="font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">
            {t("partner_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h1>
          <p className="text-slate-400 text-sm lg:text-base mb-10 max-w-2xl">{t("partner_sub")}</p>
        </Reveal>

        {appNumber ? (
          <Reveal>
            <div data-testid="partner-success" className="bg-[#0F172A] border border-white/10 p-8 lg:p-10">
              <CheckCircle2 size={28} className="text-emerald-400 mb-4" />
              <h2 className="font-display text-white text-2xl mb-2">{t("partner_success_title")}</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                {t("partner_success_body")}{" "}
                <span className="text-[#FFC61A] font-mono font-semibold">{appNumber}</span>
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  data-testid="partner-back-home"
                  to="/"
                  className="inline-block bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-6 py-3 transition-colors duration-300"
                >
                  {t("partner_back_home")}
                </Link>
                <button
                  data-testid="partner-another-btn"
                  onClick={() => { setAppNumber(null); setForm(EMPTY); }}
                  className="text-sm text-slate-300 hover:text-[#FFC61A] border border-white/15 hover:border-[#FFC61A] px-6 py-3 transition-colors duration-300"
                >
                  {t("partner_another")}
                </button>
              </div>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form
              data-testid="partner-form"
              onSubmit={submit}
              className="bg-[#0F172A] border border-white/10 p-8 lg:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Input
                  data-testid="partner-company-input"
                  required
                  placeholder={t("partner_ph_company")}
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className={field}
                />
                <Input
                  data-testid="partner-name-input"
                  required
                  placeholder={t("partner_ph_name")}
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className={field}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Input
                  data-testid="partner-email-input"
                  required
                  type="email"
                  placeholder={t("partner_ph_email")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={field}
                />
                <Input
                  data-testid="partner-phone-input"
                  placeholder={t("partner_ph_phone")}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={field}
                />
              </div>
              <select
                data-testid="partner-type-select"
                value={form.business_type}
                onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                className="w-full bg-[#0B1120] border border-white/10 text-slate-100 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ED1C24]"
              >
                <option value="">{t("partner_ph_type")}</option>
                {BUSINESS_TYPES.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <Textarea
                data-testid="partner-message-input"
                rows={4}
                placeholder={t("partner_ph_message")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={field}
              />
              <div>
                <p className="text-[11px] text-slate-500 mb-2">{t("plogo_label")}</p>
                <LogoPicker
                  testId="partner-logo"
                  value={form.logo}
                  onChange={(url) => setForm({ ...form, logo: url })}
                  uploadLabel={t("plogo_upload")}
                  uploadingLabel={t("plogo_uploading")}
                />
              </div>
              <button
                data-testid="partner-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-semibold py-4 transition-all duration-300"
              >
                {sending ? t("partner_sending") : t("partner_submit")}
              </button>
              <p className="text-[11px] text-slate-500 text-center">{t("partner_note")}</p>
            </form>
          </Reveal>
        )}
      </div>

      <Reveal delay={0.15}>
        <div data-testid="partner-cert-section" className="max-w-3xl mt-20 border-t border-white/10 pt-14">
          <div className="flex items-center gap-3 mb-3">
            <Award size={20} className="text-[#FFC61A]" />
            <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{t("pcert_label")}</p>
          </div>
          <h2 className="font-display font-semibold text-white text-3xl sm:text-4xl tracking-tight mb-3">
            {t("pcert_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h2>
          <p className="text-slate-400 text-sm mb-8 max-w-2xl">{t("pcert_sub")}</p>

          <form
            data-testid="partner-cert-form"
            onSubmit={downloadCertificate}
            className="bg-[#0F172A] border border-white/10 p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Input
                data-testid="pcert-ref-input"
                required
                placeholder={t("pcert_ph_ref")}
                value={certRef}
                onChange={(e) => { setCertRef(e.target.value); setCertState(null); }}
                className={field}
              />
              <Input
                data-testid="pcert-email-input"
                required
                type="email"
                placeholder={t("pcert_ph_email")}
                value={certEmail}
                onChange={(e) => { setCertEmail(e.target.value); setCertState(null); }}
                className={field}
              />
            </div>
            {certState === "not_found" && (
              <p data-testid="pcert-error-not-found" className="text-sm text-[#ED1C24] border-l-2 border-[#ED1C24] pl-3">
                {t("pcert_not_found")}
              </p>
            )}
            {certState === "not_ready" && (
              <p data-testid="pcert-error-not-ready" className="text-sm text-[#FFC61A] border-l-2 border-[#FFC61A] pl-3">
                {t("pcert_not_ready")}
              </p>
            )}
            {certState === "done" && (
              <p data-testid="pcert-success" className="text-sm text-emerald-400 border-l-2 border-emerald-400 pl-3">
                {t("pcert_done")}
              </p>
            )}
            <button
              data-testid="pcert-download-btn"
              type="submit"
              disabled={certState === "searching"}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#FFC61A] hover:bg-[#e6b118] disabled:opacity-60 text-[#0B1120] font-semibold py-4 transition-all duration-300"
            >
              <Download size={17} />
              {certState === "searching" ? t("cert_searching") : t("pcert_btn")}
            </button>
          </form>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div id="partner-dashboard" data-testid="partner-dashboard-section" className="max-w-3xl mt-20 border-t border-white/10 pt-14">
          <div className="flex items-center gap-3 mb-3">
            <Inbox size={20} className="text-[#FFC61A]" />
            <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{t("pdash_label")}</p>
          </div>
          <h2 className="font-display font-semibold text-white text-3xl sm:text-4xl tracking-tight mb-3">
            {t("pdash_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h2>
          <p className="text-slate-400 text-sm mb-8 max-w-2xl">{t("pdash_sub")}</p>

          {!dash ? (
            <form
              data-testid="partner-dashboard-login-form"
              onSubmit={loadDashboard}
              className="bg-[#0F172A] border border-white/10 p-8 lg:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Input
                  data-testid="pdash-ref-input"
                  required
                  placeholder={t("pcert_ph_ref")}
                  value={dashRef}
                  onChange={(e) => { setDashRef(e.target.value); setDashState(null); }}
                  className={field}
                />
                <Input
                  data-testid="pdash-email-input"
                  required
                  type="email"
                  placeholder={t("pcert_ph_email")}
                  value={dashEmail}
                  onChange={(e) => { setDashEmail(e.target.value); setDashState(null); }}
                  className={field}
                />
              </div>
              {dashState === "error" && (
                <p data-testid="pdash-error" className="text-sm text-[#ED1C24] border-l-2 border-[#ED1C24] pl-3">
                  {t("pcert_not_found")}
                </p>
              )}
              <button
                data-testid="pdash-open-btn"
                type="submit"
                disabled={dashState === "loading"}
                className="w-full bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-semibold py-4 transition-all duration-300"
              >
                {dashState === "loading" ? t("pdash_loading") : t("pdash_open")}
              </button>
            </form>
          ) : (
            <div data-testid="partner-dashboard" className="space-y-8">
              <div className="bg-[#0F172A] border border-white/10 p-6 lg:p-8 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-display text-white text-xl">{dash.company}</p>
                  <p className="text-xs text-slate-500 mt-1">{dashRef.trim().toUpperCase()}{dash.partner_type ? ` · ${dash.partner_type}` : ""}</p>
                  {dash.application_status === "approved" && (
                    <div className="mt-4">
                      <p className="text-[11px] text-slate-500 mb-2">{t("pdash_logo_hint")}</p>
                      <LogoPicker
                        testId="pdash-logo"
                        value={dash.logo}
                        onChange={saveLogo}
                        uploadLabel={t("plogo_upload")}
                        uploadingLabel={t("plogo_uploading")}
                      />
                    </div>
                  )}
                </div>
                <span data-testid="pdash-app-status" className={`text-[11px] tracking-[0.15em] uppercase border px-3 py-1.5 ${statusColor[dash.application_status] || statusColor.new}`}>
                  {dash.application_status.replace("_", " ")}
                </span>
              </div>

              {dash.application_status === "approved" && (
                <div data-testid="pdash-pricelist" className="bg-[#0F172A] border border-[#FFC61A]/20 p-6 lg:p-8 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-2">{t("pdash_pricelist_title")}</p>
                    <p className="text-sm text-slate-400">{t("pdash_pricelist_hint").replace("{pct}", dash.discount_pct ?? 10)}</p>
                  </div>
                  <a
                    data-testid="pdash-pricelist-btn"
                    href={`${API}/partner-pricelist?app_number=${encodeURIComponent(dashRef.trim())}&email=${encodeURIComponent(dashEmail.trim())}`}
                    className="inline-flex items-center gap-2 bg-[#FFC61A] hover:bg-[#e6b118] text-[#0B1120] text-sm font-semibold px-6 py-3 transition-colors duration-300"
                  >
                    <Download size={15} /> {t("pdash_pricelist_btn")}
                  </a>
                </div>
              )}

              {dash.application_status === "approved" && (
                <form
                  data-testid="partner-request-form"
                  onSubmit={submitRequest}
                  className="bg-[#0F172A] border border-white/10 p-6 lg:p-8 space-y-4"
                >
                  <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{t("pdash_new")}</p>
                  <Input
                    data-testid="pdash-req-title"
                    required
                    placeholder={t("pdash_req_title")}
                    value={reqForm.title}
                    onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })}
                    className={field}
                  />
                  <Textarea
                    data-testid="pdash-req-desc"
                    rows={3}
                    placeholder={t("pdash_req_desc")}
                    value={reqForm.description}
                    onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })}
                    className={field}
                  />
                  <FilePicker
                    testId="pdash-req-file"
                    value={reqForm.attachment}
                    onChange={(f) => setReqForm({ ...reqForm, attachment: f })}
                  />
                  <button
                    data-testid="pdash-req-submit"
                    type="submit"
                    disabled={reqSending}
                    className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-3 transition-colors duration-300"
                  >
                    <Send size={14} /> {reqSending ? t("partner_sending") : t("pdash_submit")}
                  </button>
                </form>
              )}

              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-4">{t("pdash_requests")}</p>
                {dash.requests.length === 0 ? (
                  <p data-testid="pdash-empty" className="text-sm text-slate-500 border border-white/10 p-6">{t("pdash_empty")}</p>
                ) : (
                  <div className="space-y-3">
                    {dash.requests.map((q, i) => (
                      <div key={q.id} data-testid={`pdash-request-${i}`} className="bg-[#0F172A] border border-white/10 p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                          <p className="text-slate-100 text-sm font-medium">{q.title}</p>
                          <span className={`text-[10px] tracking-[0.15em] uppercase border px-2.5 py-1 ${statusColor[q.status] || statusColor.new}`}>
                            {q.status.replace("_", " ")}
                          </span>
                        </div>
                        {q.description && <p className="text-xs text-slate-400 leading-relaxed mb-2">{q.description}</p>}
                        {q.attachment && (
                          <a
                            data-testid={`pdash-attachment-${i}`}
                            href={imgUrl(q.attachment)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 mb-2"
                          >
                            <Paperclip size={12} /> {t("patt_view")}
                          </a>
                        )}
                        <p className="text-[10px] text-slate-600 font-mono">{q.req_number} · {new Date(q.created_at).toLocaleDateString()}</p>
                        {q.admin_notes && (
                          <p data-testid={`pdash-notes-${i}`} className="text-xs text-[#FFC61A] border-l-2 border-[#FFC61A] pl-3 mt-3">
                            {t("pdash_notes")}: {q.admin_notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Reveal>
    </main>
  );
}
